import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "components/app/dialog/dialog";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { X, Plus, Loader } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";

import { useAppSelector } from "store/store";
import axios from "axios";
import { RequestApiDisableHead } from "utils/requestApi";
import { Snackbar } from "components";
import { Skeleton } from "components/ui/skeleton";

const ffmpeg = new FFmpeg();

interface DialogProps {
  onUploadSuccess: (value: any) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
  soundData: any;
}

export default function DialogUploadSoundMusic({ isDialogOpen, setIsDialogOpen, onUploadSuccess, soundData }: DialogProps) {
  const [initLoading, setInitLoading] = useState(false);
  const [processingLoading, setProcessingLoading] = useState(false);
  const [isUpLoading, setIsUpLoading] = useState(false);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputFile, setOutputFile] = useState<File | any>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null); // State for the thumbnail image
  const { token } = useAppSelector((state) => state.auth);
  const selectedFile = useRef<any>();
  const [soundDuration, setSoundDuration] = useState<number | null>(null);

  useEffect(() => {
    if (isDialogOpen) {
      const loadFFmpeg = async () => {
        if (!ffmpeg.loaded) {
          setInitLoading(true);
          await ffmpeg.load();
          setInitLoading(false);
        }
      };
      loadFFmpeg();
    }
  }, [isDialogOpen]);

  const handleClear = () => {
    setOutputUrl(null);
    setOutputFile(null);
    setThumbnail(null);
    setSoundDuration(null); // Clear thumbnail when dialog is closed
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    handleClear();
  };

  const getMediaDuration = (url: string) => {
    const media: any = document.createElement("audio");
    media.src = url;

    media.addEventListener("loadedmetadata", () => {
      setSoundDuration(media.duration.toFixed(0));
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    selectedFile.current = event.target.files?.[0];
    if (selectedFile.current) {
      setProcessingLoading(true);
      try {
        // Convert file to ArrayBuffer and write it to FFmpeg FS
        const fileBuffer = await selectedFile.current.arrayBuffer();
        await ffmpeg.writeFile(selectedFile.current.name, new Uint8Array(fileBuffer));

        // Execute FFmpeg command to convert to m4a format
        await ffmpeg.exec(["-i", selectedFile.current.name, "-vn", "-acodec", "aac", "output.m4a"]);

        const data: any = await ffmpeg.readFile("output.m4a");
        const audioBlob = new Blob([data.buffer], { type: "audio/m4a" });
        const audioUrl = URL.createObjectURL(new Blob([data.buffer], { type: "audio/m4a" }));
        getMediaDuration(audioUrl);
        const audioFile = new File([audioBlob], "output.m4a", {
          type: "audio/m4a",
        });

        setOutputFile(audioFile);
        setOutputUrl(audioUrl);
        setProcessingLoading(false);
      } catch (error) {
        // Handle errors
        console.error("Error processing file:", error);
        setProcessingLoading(false);
      }
    }
  };

  const handleUpload = async () => {
    setIsUpLoading(true);
    // APIRequest
    let storageHostClusterId = "";
    let hostName = "";
    let url: string = "";
    let uploadFile: any = "";
    let musicUrl: string = "";
    let musicSrc: string = "";

    if (!token) {
      throw new Error("Invalid or missing token");
    }

    url = RouteApi.getStorageHostCluster;
    url += `token=${token}`;

    await RequestApi(url, {
      data: {
        token,
        file_size: outputFile?.size,
      },
    })
      .then((responses) => {
        if (responses.status === "success") {
          storageHostClusterId = responses.storage_host_cluster.id;
          hostName = responses.storage_host_cluster.host_name;
        }
      })
      .catch((error) => {
        AppLog.show({
          message: `[handleUpload]`,
          status: StatusMsg.error,
          error,
        });
      });

    let url1 = `${hostName}/api-mobile-flipflop/v1/submit-upload-files?`;
    url1 += `token=${token}`;
    url1 += `&storage_host_cluster_id=${storageHostClusterId}`;

    const formdata = new FormData();
    formdata.append("file_music", outputFile);

    const reqOptions = {
      url: url1,
      method: "POST",
      data: formdata,
    };
    await axios
      .request(reqOptions)
      .then((res) => {
        uploadFile = res.data.upload_files;
        return res;
      })
      .catch((err) => {
        console.log(JSON.stringify(err));
      });

    let url2 = `${hostName}/api-mobile-flipflop/v1/submit-update-music-url?`;
    url2 += `token=${token}`;
    musicUrl = uploadFile[0]["attachment_url"];
    musicSrc = uploadFile[0]["attachment_source"];

    await RequestApiDisableHead(url2, {
      data: {
        sound_id: soundData?.sound_id,
        sound_duration: soundDuration,
        sound_url: musicUrl,
        sound_src: musicSrc,
        provider_storage_host_id: storageHostClusterId,
      },
    })
      .then((res) => {
        if (res.data.status === "success") {
          onUploadSuccess(res.data.music);
          handleCloseDialog();
        } else {
        }
        setIsUpLoading(false);
      })
      .catch((error) => {
        AppLog.show({
          message: `[handleUpload]`,
          status: StatusMsg.error,
          error,
        });
        setIsUpLoading(false);
      });
  };

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="p-0 min-w-[560px]">
        <DialogHeader className="border-b-2 mt-4 pb-4">
          <DialogTitle className="text-center">Upload Music</DialogTitle>
          <div
            className="flex bg-gray-200 h-[28px] justify-center rounded-full w-[28px] absolute cursor-pointer dark:bg-accent items-center right-4 top-1 z-40"
            onClick={handleCloseDialog}
          >
            <X />
          </div>
        </DialogHeader>
        <div className="p-4">
          <div className="flex justify-center w-full">
            {initLoading && <Skeleton className="h-28 w-full" />}
            {!initLoading && !processingLoading && !outputUrl && (
              <Label htmlFor="file" className="flex border justify-center rounded-lg w-full cursor-pointer items-center py-10">
                <Plus />
                <p>Select Media</p>
              </Label>
            )}

            {outputUrl && (
              <div className="text-center mt-4">
                <audio controls src={outputUrl} className="my-4" />
              </div>
            )}
            {processingLoading && <p className="text-green-500">Processing...</p>}

            <input id="file" type="file" onChange={handleFileChange} hidden accept="audio/*, video/*" />
          </div>

          <DialogFooter className="flex items-center mt-4">
            {outputUrl && (
              <Button disabled={isUpLoading} variant="destructive" className="" onClick={handleClear}>
                Clear
              </Button>
            )}
            {outputUrl && (
              <Button className="" onClick={handleUpload}>
                {isUpLoading ? <Loader className="animate-spin" /> : "Upload"}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
